import { useEffect, type ComponentType } from "react";
import { BrandMark } from "@/shared/ui/core";
import { Link } from "react-router-dom";
import {
  LifeBuoy,
  LogIn,
  LogOut,
  ScrollText,
  ShieldCheck,
  ShoppingBag,
  Store,
  UserPlus,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/ui/core";

export interface SiteMenuItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

export interface SiteMenuPanelProps {
  id: string;
  open: boolean;
  onClose: () => void;
  /** Si no se pasan, se usa el mapa de navegación del sitio. */
  items?: readonly SiteMenuItem[];
  /**
   * La sesión, si la hay: con ella, "Iniciar sesión" y "Crear cuenta"
   * desaparecen (ya entró), aparece la puerta a su panel si tiene uno, y
   * el pie del menú ofrece SALIR (Anahí, 27-ago).
   */
  user?: { name: string; businessTo?: string } | null;
  onLogout?: () => void;
}

/**
 * El mapa de navegación del sitio. Se declara como DATOS y no como JSX
 * repetido: añadir una sección es una línea, y todas se pintan iguales.
 */
const DEFAULT_ITEMS: readonly SiteMenuItem[] = [
  { to: "/shop", label: "Tienda", icon: ShoppingBag },
  { to: "/franquicias", label: "Franquicias", icon: Store },
  { to: "/login", label: "Iniciar sesión", icon: LogIn },
  { to: "/register", label: "Crear cuenta", icon: UserPlus },
  { to: "/soporte", label: "Soporte", icon: LifeBuoy },
  { to: "/legal#privacidad", label: "Aviso de privacidad", icon: ShieldCheck },
  { to: "/legal#terminos", label: "Términos y condiciones", icon: ScrollText },
];

/** Las entradas de acceso sobran cuando ya se entró. */
const ACCESS_ROUTES = new Set(["/login", "/register"]);

function SiteMenuLink({ item, onClose }: { item: SiteMenuItem; onClose: () => void }) {
  const { t } = useTranslation();
  const Icon = item.icon;

  return (
    <Link
      to={item.to}
      onClick={onClose}
      className="flex items-center gap-3 rounded-2xl px-3 py-2.5 text-[13px] font-semibold text-ds-ink-3 transition hover:bg-ds-mist/70 hover:text-ds-violet-pop"
    >
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-ds-mist/70 text-ds-violet-pop">
        <Icon className="h-4 w-4" />
      </span>
      {t(item.label)}
    </Link>
  );
}

/**
 * El panel que abre el botón de menú.
 *
 * Se cierra con Escape además de con el fondo y la X: quien navega con
 * teclado no tiene forma de "hacer clic fuera", y sin Escape quedaría
 * atrapado. El fondo es un `<button>` de verdad, no un `<div onClick>`,
 * para que también sea alcanzable.
 *
 * No se monta cuando está cerrado: así sus enlaces no participan en el
 * orden de tabulación de la página que hay debajo.
 */
export function SiteMenuPanel({
  id,
  open,
  onClose,
  items = DEFAULT_ITEMS,
  user,
  onLogout,
}: SiteMenuPanelProps) {
  const { t } = useTranslation();

  /*
   * Con sesión: fuera "Iniciar sesión"/"Crear cuenta" —invitar a entrar
   * a quien ya entró es ruido— y, si la persona tiene panel propio (el
   * franquiciatario), su puerta aparece arriba del todo.
   */
  const visibleItems = user
    ? [
        ...(user.businessTo
          ? [{ to: user.businessTo, label: "Mi negocio", icon: Store }]
          : []),
        ...items.filter((item) => !ACCESS_ROUTES.has(item.to)),
      ]
    : items;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center p-4 sm:p-6">
      <button
        type="button"
        onClick={onClose}
        aria-label={t("Cerrar menú")}
        className="absolute inset-0 h-full w-full cursor-default bg-ds-ink/30 backdrop-blur-sm"
      />

      <div
        id={id}
        role="dialog"
        aria-modal="true"
        aria-label={t("Menú de navegación")}
        /*
         * El MISMO vidrio que la píldora de la barra (Anahí, 27-ago):
         * blanco translúcido + desenfoque de lo que queda detrás, en
         * vez del blanco sólido — el menú y la barra son una familia.
         */
        className="relative z-10 w-full max-w-sm animate-apple-fade-in rounded-ds-xl border border-ds-border bg-white/80 p-5 shadow-ds-float backdrop-blur-xl"
      >
        <div className="flex items-start justify-between gap-4">
          {/* El imagotipo YA trae el nombre: repetirlo en versalitas
              debajo decía "It's The Best" dos veces (Anahí, 27-ago). */}
          <BrandMark height={24} />
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            onClick={onClose}
            aria-label={t("Cerrar menú")}
            leftIcon={<X />}
          />
        </div>

        <p className="mt-5 text-ds-label font-semibold uppercase tracking-ds-label text-ds-ink-3">
          {t("Navegación")}
        </p>

        <nav className="mt-2 grid gap-0.5">
          {visibleItems.map((item) => (
            <SiteMenuLink key={item.to} item={item} onClose={onClose} />
          ))}
        </nav>

        {/* El pie de la sesión: quién está dentro, y la salida. */}
        {user && onLogout && (
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-ds-border pt-4">
            <p className="truncate text-[13px] font-medium text-ds-ink">
              {t("Hola, {{name}}", { name: user.name })}
            </p>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<LogOut />}
              onClick={() => {
                onClose();
                onLogout();
              }}
            >
              {t("Salir")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
